import moment from 'moment';

import * as vp from 'vopay';
import * as Yup from 'yup';
import { ElinxFieldId, PaymentInformation, RenderedOptions } from '../types';
import styles from '../AccountingSystemOpen.module.scss';
import { ElinxRenderBag } from '../Elinx/useElinxRows';
import RemainingAmount from '../RemainingAmount';
import {
  ExtraOptionsInfo,
  getAvailablePaymentMethodLabel,
  getAvailablePaymentMethodVisibility,
  getAvailablePaymentMethodsRenderOptions,
  roundCurrency,
  isElinxActive,
  isFutureScheduled,
} from '../utils';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';

export type BuildPaymentInfoRowsProps = {
  type: 'Payable' | 'Receivable';
  maximumDate?: string;
  currency: string;
  totalAmount: number;
  paymentMethodsHeader: ({ close }: { close: () => void }) => React.ReactNode | undefined;
  paymentMethods: vp.SelectOption<ExtraOptionsInfo>[];
  creditNotes: vp.CreditNote[];
};
export type PaymentInfoRows = {
  label?: string;
  subtitle?: string;
  id?: vp.PaymentInfoId | ElinxFieldId;
  require: (ctx: PaymentInformation) => boolean;
  visible: (ctx: PaymentInformation) => boolean;
  validate?: (ctx: PaymentInformation) => unknown;
  disabled?: (ctx: PaymentInformation) => boolean;
  preset?: keyof vp.TextFieldPresets | undefined;
  type?:
    | 'input'
    | 'select'
    | 'selectFinanacialInstitution'
    | 'googleAddress'
    | 'radio'
    | 'date'
    | 'multiSelect'
    | 'checkbox'
    | 'elinx'
    | 'elinxSelect'
    | 'elinxMultiSelect';
  placeholder?: string;
  getSelectOptions?: (ctx: PaymentInformation) => vp.SelectOption<ExtraOptionsInfo>[];
  getMultiSelectOptions?: (ctx: PaymentInformation) => vp.SelectOption<any>[];
  getElinxOptions?: (ctx: PaymentInformation) => RenderedOptions[];
  render?: (bag: ElinxRenderBag) => React.ReactNode;
  getElementLeft?: (ctx: PaymentInformation) => JSX.Element;
  getElementRight?: (ctx: PaymentInformation) => JSX.Element;
  renderOption?: (o: vp.SelectOption<ExtraOptionsInfo>, args: vp.SelectRenderOptionArgs) => React.ReactNode;
  renderSelectedValue?: (o: vp.SelectOption<ExtraOptionsInfo> | null, args?: { value: string }) => React.ReactNode;

  optionVisibility?: (o: vp.SelectOption<ExtraOptionsInfo> | null) => boolean;
  renderDropdownHeader?: ({ close }: { close: () => void }) => React.ReactNode;
  container: 'field' | 'wrapper' | 'inputs';
  showValue?: boolean;
  min?: number;
  paymentRows: PaymentInfoRows[];
  initialValue?: string;
  disabledMessage?: string;
};

export const useBuildPaymentInfoRows = ({
  type,
  paymentMethods,
  totalAmount,
  currency,
  creditNotes,
  paymentMethodsHeader,
}: BuildPaymentInfoRowsProps): PaymentInfoRows[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Dialogs.SingleDialog' });

  return [
    {
      label: type === 'Receivable' ? t('collectionDate') : t('paymentDate'),
      id: vp.PaymentInfoId.PaymentDate,
      require: () => false,
      visible: () => true,

      type: 'date',
      container: 'field',
      placeholder: '',
      preset: 'text',
      paymentRows: [],
      validate: () => Yup.string().required(),
      min: moment().startOf('day').toDate().getTime(),
    },
    {
      label: type === 'Receivable' ? t('collectionCreditNotes') : t('paymentCreditNotes'),
      id: vp.PaymentInfoId.CreditNotes,
      require: () => false,
      visible: () => true,
      placeholder:
        creditNotes.length === 0 || creditNotes.every((creditNote) => creditNote.remainingCredit > totalAmount)
          ? t('creditNotesPlaceholder')
          : t('creditNotesPlaceholderChoose'),
      disabled: () =>
        creditNotes.length === 0 || creditNotes.every((creditNote) => creditNote.remainingCredit > totalAmount),
      getMultiSelectOptions: (ctx) =>
        creditNotes.map((creditNote) => ({
          label: `${creditNote.currency} ${vp.formattedCurrency(String(creditNote.remainingCredit))}`,
          value: creditNote.id,
          isDisabled:
            creditNote.remainingCredit > Number(totalAmount) - Number(ctx.CreditAmount) &&
            !ctx.CreditNotes.split(',').includes(creditNote.id),
        })),
      type: 'multiSelect',
      container: 'field',
      preset: 'text',
      paymentRows: [],
      min: moment().startOf('day').toDate().getTime(),
    },
    {
      label: t('amount', { defaultValue: 'Amount' }),
      require: () => false,
      visible: () => true,
      container: 'inputs',
      paymentRows: [
        {
          id: vp.PaymentInfoId.PaymentAmount,
          require: () => false,
          visible: () => true,
          type: 'input',
          container: 'field',
          placeholder: '',
          preset: 'currency',
          paymentRows: [],
          getElementLeft: () => (
            <div
              className={styles.InputText}
              style={{
                paddingLeft: '12px',
                display: 'inline-block',
                width: '45px',
                position: 'relative',
                marginRight: '-11px',
              }}
            >
              {currency}
            </div>
          ),
          disabled: (ctx) => isFutureScheduled(ctx.PaymentDate),
          disabledMessage: t('collectionMethodAmountDisabled'),
          validate: (ctx) =>
            Yup.number()
              .required()
              .transform((_value, originalValue: string) => Number(vp.sanitizeToNumeric(originalValue)))
              .max(
                roundCurrency(Number(totalAmount) - ctx.CreditAmount),
                t('maxAmountValidation', {
                  currency,
                  amount: vp.formattedCurrency(String(roundCurrency(Number(totalAmount) - ctx.CreditAmount))),
                }),
              )
              .test('min', t('minAmountValidation'), (value) => (ctx.CreditAmount === 0 ? (value || 0) >= 0.01 : true)),
        },
        {
          require: () => false,
          visible: () => true,
          type: 'elinx',
          container: 'field',
          paymentRows: [],
          render: ({ values }) => (
            <RemainingAmount
              totalAmount={totalAmount}
              currency={currency}
              paymentAmount={values.PaymentAmount}
              creditAmount={values.CreditAmount}
            />
          ),
        },
      ],
    },
    {
      label: type === 'Receivable' ? t('collectFrom', { defaultValue: 'Collect From' }) : t('payFrom', { defaultValue: 'Pay From' }),
      id: vp.PaymentInfoId.PaymentMethod,
      require: () => false,
      visible: () => true,
      getSelectOptions: () => paymentMethods,
      renderOption: getAvailablePaymentMethodsRenderOptions,
      renderSelectedValue: (option, args) =>
        args?.value === 'eLinx'
          ? paymentMethods.find((method) => method.value === 'eLinx')?.renderedValue
          : getAvailablePaymentMethodLabel(option),
      optionVisibility: getAvailablePaymentMethodVisibility,
      renderDropdownHeader: paymentMethodsHeader,
      type: 'select',
      container: 'field',
      placeholder: '',
      preset: 'text',
      paymentRows: [],
      validate: (ctx) => (isElinxActive(ctx) ? Yup.string().notRequired() : Yup.string().required()),
    },
  ];
};
